import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import type { EpcScheduleRevisionSummary } from '../../../../services/epcProjectService';
import { PmoCard, PmoCardHeader } from './ui/PmoCard';

function fmtDay(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'dd MMM yy');
}

function deltaClass(n: number): string {
  if (n > 0) return 'font-bold text-[#be123c]';
  if (n < 0) return 'font-bold text-[#047857]';
  return 'text-[#94a3b8]';
}

function signed(n: number, suffix = ''): string {
  if (n === 0) return `0${suffix}`;
  return `${n > 0 ? '+' : '−'}${Math.abs(n)}${suffix}`;
}

export function RevisionLogCard({
  revisions,
  unavailable,
}: {
  revisions: EpcScheduleRevisionSummary[];
  unavailable: boolean;
}) {
  return (
    <PmoCard>
      <PmoCardHeader
        title="Revision log · vs baseline"
        action={
          <Link to="../schedule" className="text-[11px] font-semibold text-[#006346] hover:underline">
            Schedule tab →
          </Link>
        }
      />
      {unavailable || revisions.length === 0 ? (
        <p className="px-4 py-5 text-[12px] text-[#94a3b8]">
          {unavailable
            ? 'Revision history unavailable'
            : 'No schedule imported yet — the revision log fills in after the first MPP import.'}
        </p>
      ) : (
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="text-left text-[9.5px] font-bold uppercase tracking-[0.1em] text-[#94a3b8]">
              <th className="px-3 py-2">Rev</th>
              <th className="px-3 py-2">Imported</th>
              <th className="px-3 py-2">By</th>
              <th className="px-3 py-2 text-right">Tasks</th>
              <th className="px-3 py-2">Plan finish</th>
              <th className="px-3 py-2 text-right">Δ Finish</th>
              <th className="px-3 py-2 text-right">Δ Tasks</th>
            </tr>
          </thead>
          <tbody>
            {revisions.map((r) => (
              <tr key={r.version} className="tabular-nums hover:bg-[#f9fbf9]">
                <td className="border-t border-[#f4f6f8] px-3 py-2.5 font-bold text-slate-900">
                  R{r.version}
                  {r.isBaseline ? ' · Baseline' : r.isCurrent ? ' · Current' : ''}
                </td>
                <td className="border-t border-[#f4f6f8] px-3 py-2.5 text-slate-600">{fmtDay(r.importedAt)}</td>
                <td className="border-t border-[#f4f6f8] px-3 py-2.5 text-slate-600">{r.createdByName}</td>
                <td className="border-t border-[#f4f6f8] px-3 py-2.5 text-right text-slate-600">{r.taskCount}</td>
                <td className="border-t border-[#f4f6f8] px-3 py-2.5 text-slate-600">{fmtDay(r.planFinish)}</td>
                <td className={`border-t border-[#f4f6f8] px-3 py-2.5 text-right ${r.isBaseline ? 'text-[#94a3b8]' : deltaClass(r.deltaFinishDays)}`}>
                  {r.isBaseline ? '—' : signed(r.deltaFinishDays, ' days')}
                </td>
                <td className={`border-t border-[#f4f6f8] px-3 py-2.5 text-right ${r.isBaseline ? 'text-[#94a3b8]' : deltaClass(r.deltaTaskCount)}`}>
                  {r.isBaseline ? '—' : signed(r.deltaTaskCount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PmoCard>
  );
}
